import type { VoucherCodeResponse } from '@/api/model/voucherCodeResponse';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, MapPin } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import React from 'react';
// PDF/Email flow removed with order removal

interface VoucherSuccessStageProps {
  code: VoucherCodeResponse;
  onClose: () => void;
}

export const VoucherSuccessStage: React.FC<VoucherSuccessStageProps> = ({ code, onClose: _onClose }) => {
  const voucher = code.voucher;
  const voucherCode = code.code;
  const sanitizedVoucherCode = voucherCode?.trim();
  const hasVoucherCode = Boolean(sanitizedVoucherCode);
  const pdfUrl = code.pdfUrl ?? null;

  if (!voucher) {
    return null;
  }

  const numericValue = typeof voucher.value === 'number' ? voucher.value : Number(voucher.value);
  const formattedValue = Number.isFinite(numericValue)
    ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(numericValue)
    : '';

  return (
    <div className='flex flex-col items-center text-center space-y-6 mx-auto p-6 pb-10'>
      {/* Success Icon */}
      <div className='relative mt-4'>
        <div className='w-20 h-20 rounded-full flex items-center justify-center'>
          <svg width='96' height='96' viewBox='0 0 96 96' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M41.1716 4.51149C50.4928 3.04303 60.0212 4.61381 68.3592 8.96073L70.0076 9.86697L70.3516 10.0857C71.9972 11.2704 72.5108 13.5336 71.4764 15.3279C70.4412 17.1221 68.2244 17.8115 66.3748 16.9802L66.0116 16.7928L64.66 16.0545C57.8388 12.4984 50.0436 11.2127 42.418 12.4138C34.2841 13.6953 26.8339 17.7272 21.3124 23.8357C15.7909 29.9445 12.5298 37.7625 12.0741 45.984C11.6186 54.206 13.9956 62.338 18.8085 69.0192C23.6213 75.7004 30.5809 80.5308 38.5233 82.7028C46.4656 84.8748 54.914 84.2588 62.4568 80.9568C70.0004 77.6548 76.1844 71.864 79.9764 64.5544C83.5312 57.702 84.7824 49.9008 83.57 42.3044L83.2928 40.7888L83.2344 40.3828C83.0328 38.3661 84.3856 36.4841 86.414 36.0701C88.444 35.6565 90.4284 36.8602 91.0312 38.7966L91.1328 39.1912L91.4688 41.0428C92.9504 50.3284 91.4196 59.8624 87.074 68.238C82.4392 77.1716 74.8836 84.2492 65.664 88.2848C56.4452 92.3204 46.1212 93.072 36.4139 90.4176C26.7063 87.7628 18.1987 81.8612 12.3163 73.6952C6.43398 65.5288 3.52919 55.5916 4.08582 45.5428C4.64262 35.4938 8.63022 25.9389 15.3788 18.4724C22.1273 11.0064 31.2304 6.07777 41.1716 4.51149Z'
              fill='var(--color-swd-gruen)'
            />
            <path
              d='M85.4768 12.8985C87.0476 11.6171 89.3636 11.7074 90.828 13.1719C92.2928 14.6364 92.3832 16.9524 91.1016 18.5235L90.828 18.8282L50.828 58.828C49.3636 60.2924 47.0476 60.3828 45.4768 59.1016L45.172 58.828L33.1719 46.828L32.8985 46.5236C31.6171 44.9524 31.7074 42.6364 33.1719 41.172C34.6364 39.7074 36.9524 39.6171 38.5235 40.8984L38.8282 41.172L48 50.3436L85.172 13.1719L85.4768 12.8985Z'
              fill='var(--color-swd-gruen)'
            />
          </svg>
        </div>
      </div>

      {/* Title */}
      <h1 className='text-2xl font-semibold text-swd-tuerkis-dark leading-8'>Gutschein erfolgreich aktiviert!</h1>

      {/* Provider Info */}
      <div className='space-y-3'>
        <h2 className='text-xl font-medium text-gray-600'>{voucher.provider.name}</h2>
        {voucher.provider.address && (
          <div className='flex items-center justify-center gap-2 text-gray-600'>
            <MapPin className='w-4 h-4 flex-shrink-0' />
            <span className='text-sm font-medium'>{voucher.provider.address}</span>
          </div>
        )}
      </div>

      {/* Value badge */}
      {formattedValue && (
        <div className='flex gap-2 justify-center'>
          <Badge className='bg-swd-gruen-light text-swd-tuerkis-dark text-sm font-bold px-3 py-1.5 rounded-lg border-0'>
            {formattedValue}
          </Badge>
        </div>
      )}

      {/* Voucher Code Section */}
      {hasVoucherCode && sanitizedVoucherCode && (
        <div className='space-y-3 w-full'>
          <p className='text-sm text-gray-600 font-medium'>Ihr Gutscheincode:</p>
          <div className='text-3xl font-bold text-teal-700 font-mono tracking-wider'>{sanitizedVoucherCode}</div>
          <p className='text-sm text-gray-500'>Zeigen Sie diesen Code beim Anbieter vor</p>
        </div>
      )}

      {hasVoucherCode && sanitizedVoucherCode && (
        <>
          {/* Divider */}
          <div className='w-full flex items-center justify-center my-4'>
            <div className='flex-1 border-t border-gray-200'></div>
            <span className='px-4 text-sm text-gray-500'>ODER</span>
            <div className='flex-1 border-t border-gray-200'></div>
          </div>

          <p className='text-sm text-gray-600'>QR-Code scannen:</p>

          {/* QR Code */}
          <div className='p-4 border-2 border-cyan-300 rounded-2xl bg-white'>
            <QRCodeSVG value={sanitizedVoucherCode} size={200} bgColor='white' fgColor='black' level='M' />
          </div>
        </>
      )}

      {pdfUrl && (
        <Button asChild variant='outline' className='mt-2 flex items-center gap-2'>
          <a href={pdfUrl} target='_blank' rel='noopener noreferrer'>
            <Download className='w-4 h-4' />
            PDF herunterladen
          </a>
        </Button>
      )}
    </div>
  );
};
